import type { PartnerSection } from './types';

/**
 * Partner (firm / `CLIENT`) guide — how a vehicle gets from a partner's own system into a Bia
 * auction, and what happens to it here afterwards.
 *
 * This tab exists because the submission path is **not** in the admin UI. A firm cannot create
 * a vehicle from these screens: `vehicleEdit` is ADMIN/DEVELOPER/EDITOR, and the
 * `FIRM_*` roles are read-only on sales and vehicles. Vehicles arrive through the ingestion
 * API (`X-Api-Key`, ADR 0017) and become lots only when a moderator approves them. So the
 * people who submit and the people who watch are different people, and neither of them could
 * previously read the rules anywhere in the product.
 *
 * M13 (ADR 0026) moved the key itself: it belongs to the **firm** and is issued by an admin
 * from the firm's page (`h-firms`), not minted self-service by a CLIENT account. `p-auth`
 * documents the partner half of that; the operator half is in the handbook.
 *
 * Two layers on purpose (`section.layer`): `business` is what the fleet manager prepares,
 * `technical` is what their developer wires up. Facts here were read off the schema and
 * routes — `vehicleSubmitSchema` (`packages/shared-schema/src/ingestion.ts`), the caps in
 * `vehicle.ts`, `apps/api/src/routes/client.ts` and the approve path in
 * `apps/api/src/routes/submissions.ts`. When any of those change, this changes with them.
 */
export const PARTNER: PartnerSection[] = [
  {
    id: 'p-flow',
    title: 'Aracınız ihaleye nasıl giriyor?',
    intro: 'Gönderimden satışa kadar izlenen yol.',
    layer: 'business',
    topics: [
      {
        id: 'p-pipeline',
        title: 'Sürecin tamamı',
        intro:
          'Araç, kendi sisteminizden bize gönderilir; bir yetkilimiz inceler; onaylanırsa hedeflediğiniz ihaleye sıradaki araç olarak eklenir.',
        steps: [
          {
            do: 'Aracı, henüz listelenmemiş bir ihaleyi seçerek gönderirsiniz.',
            note: 'Kendi sisteminizden entegrasyon üzerinden. Bu ekranlardan araç ekleyemezsiniz — yönetim panelinde firma kullanıcıları izleyicidir, veri girişi yapmaz.',
          },
          {
            do: 'Araç “Bekliyor” durumuna düşer.',
            note: 'Bu aşamada henüz ihalede değildir ve katılımcılara görünmez.',
          },
          {
            do: 'Bia yetkilisi başvuruyu inceler.',
            note: 'Gönderdiğiniz her şeyi görür: teknik bilgiler, ekspertiz, fotoğraflar ve gizli rezerv dahil.',
          },
          {
            do: 'Onaylanırsa araç ihaleye “Sırada” olarak eklenir; ihalenin takvimi buna göre yeniden hesaplanır.',
          },
          {
            do: 'İhale günü araç açılır, teklif alır ve “Satıldı” ya da “Satılmadı” olarak kapanır.',
          },
        ],
        warnings: [
          'Araç gönderebileceğiniz pencere ihalenin **başlangıç saatine değil, listeleme saatine** kadardır: ihale vitrine düştüğü anda gönderime kapanır. Listeleme saati geçmiş bir ihale, başlamasına günler olsa bile uygun ihaleler listesinden çıkar ve göndermeye çalışırsanız reddedilir.',
          'Bu sınır bilerek konmuştur: listeleme anı ihalenin canlıya alınma tetiğidir, o noktadan sonra gelen bir aracı yetkilimiz zaten onaylayamazdı. Kabul edilen her gönderim, onaylanabilecek bir gönderimdir.',
          'Onay ayrı bir “yayınlama” adımı gerektirmez: yetkilimiz onayladığı anda araç ihaledeki yerini alır.',
        ],
        see: ['p-fields', 'p-status', 'g-submission'],
      },
      {
        id: 'p-eligibility',
        title: 'Hangi araç gönderilebilir?',
        intro: 'Reddedilmeden önce kendiniz eleyebileceğiniz şartlar.',
        steps: [
          { do: 'Plakası olmalı ve bu plaka başka bir iş ortağına kayıtlı olmamalı.' },
          { do: 'Aynı aracın halihazırda bekleyen ya da ihalede olan bir başvurusu olmamalı.' },
          { do: 'Başlangıç fiyatı sıfırdan büyük olmalı — bu aynı zamanda ihale uygunluk şartıdır.' },
        ],
        warnings: [
          'Bir kez satılmış araç yeniden gönderilemez. Satılmayan araç ise gönderilebilir; sistem aynı araç kaydını korur, geçmişi kaybolmaz.',
        ],
        see: ['p-errors', 'g-unsold'],
      },
    ],
  },

  {
    id: 'p-prepare',
    title: 'Göndermeden önce hazırlayın',
    intro: 'Aracı iyi satan bilgi ile zorunlu bilgi aynı şey değil — ikisi de burada.',
    layer: 'business',
    topics: [
      {
        id: 'p-fields',
        title: 'Bilgi listesi ve önem sırası',
        intro:
          '“Zorunlu” olmadan gönderim reddedilir. “Önemli” olmadan gönderim geçer ama araç ya onaylanamaz ya da alıcıda karşılık bulmaz. “İsteğe bağlı” alanlar aracı rakiplerinden ayırır.',
        fields: [
          { name: 'auctionId', label: 'Hedef ihale', requirement: 'required', note: 'Yayınlanmış ve **henüz listelenmemiş** bir ihale olmalı; uygun ihaleler listesinden alın.' },
          { name: 'plate', label: 'Plaka', requirement: 'required' },
          { name: 'chassis', label: 'Şasi numarası', requirement: 'recommended', note: 'Aracın değişmeyen kimliği; plaka değişse de aynı aracı işaret eder.' },
          {
            name: 'startingPrice',
            label: 'Başlangıç fiyatı',
            requirement: 'required',
            note: 'Kuruş cinsinden tam sayı. Katılımcının gördüğü tek fiyat budur; sıfırdan büyük olmalı.',
          },
          {
            name: 'reserve',
            label: 'Gizli rezerv (taban)',
            requirement: 'recommended',
            note: 'Kesin satış seçmediyseniz fiilen zorunludur: rezervsiz normal araç onaylanamaz. Katılımcıya asla gösterilmez.',
          },
          {
            name: 'saleMode',
            label: 'Satış tipi',
            requirement: 'recommended',
            note: '“guaranteed” = kesin satış; en yüksek teklifi verene satılır ve rezerv aranmaz. Boş bırakılırsa “normal” kabul edilir.',
          },
          { name: 'increment', label: 'Artış adımı (yedek)', requirement: 'optional', note: 'Kuruş. Yalnızca ihalenin barem tablosu yoksa işler; barem varsa barem kazanır. Boş bırakılırsa 100,00 ₺.' },
          { name: 'currency', label: 'Para birimi', requirement: 'optional', note: 'Boş bırakılırsa TRY.' },
          { name: 'brand / series / modelName', label: 'Marka, seri, model', requirement: 'recommended', note: 'Araç listelerinde ve aramada aracın adını bunlar oluşturur.' },
          { name: 'year / km / color', label: 'Yıl, kilometre, renk', requirement: 'recommended', note: 'Alıcının ilk baktığı üç bilgidir; araç kartında öne çıkar.' },
          { name: 'fuel / gear / bodyType / vehicleType', label: 'Yakıt, vites, kasa, araç tipi', requirement: 'recommended' },
          { name: 'engineCapacity / enginePower', label: 'Motor hacmi (cc) ve gücü (hp)', requirement: 'optional' },
          { name: 'condition', label: 'Durum', requirement: 'optional', note: 'new · used · free-certified' },
          { name: 'description', label: 'Açıklama', requirement: 'recommended', note: 'En fazla 5000 karakter. Araç sayfasında katılımcının okuduğu serbest metindir.' },
          { name: 'expertise', label: 'Ekspertiz raporu', requirement: 'recommended', note: '14 parçanın boya/değişen durumu. Tamamı orijinal işaretlenirse araç “Orijinal” rozeti alır — rozet elle verilemez.' },
          { name: 'equipments / checklist', label: 'Donanım ve teslim kontrol listesi', requirement: 'optional' },
          { name: 'mediaUrls', label: 'Fotoğraf adresleri', requirement: 'recommended', note: 'En fazla 24 adres. Fotoğrafsız araç ilgi görmez.' },
          { name: 'clientRef', label: 'Kendi referansınız', requirement: 'optional', note: 'Sizin stok numaranız; bizde saklanır ve yetkilimize gösterilir. Eşleştirmeyi kolaylaştırır.' },
          { name: 'properties', label: 'Serbest ek bilgiler', requirement: 'optional', note: 'Buradaki alanlara oturmayan kendi verilerinizi taşıyabileceğiniz serbest yapıda bir nesne. Saklanır ve yetkilimize gösterilir; araç sayfasında yayımlanmaz.' },
        ],
        warnings: [
          'Bütün para alanları **kuruş cinsinden tam sayıdır**. 150.000 ₺ göndermek için `15000000` yazın — ondalık ya da metin gönderirseniz istek reddedilir.',
        ],
        see: ['p-price', 'p-media', 'g-reserve', 'g-expertise'],
      },
      {
        id: 'p-price',
        title: 'Üç fiyat alanı ve aralarındaki fark',
        intro: 'En sık karıştırılan konu bu; onay hatalarının çoğu buradan çıkıyor.',
        steps: [
          {
            do: 'Başlangıç fiyatı — herkesin gördüğü açılış fiyatı.',
            note: 'Teklifler buradan yukarı çıkar. Düşük tutmak katılımı artırır.',
          },
          {
            do: 'Rezerv — yalnızca sizin ve Bia yetkilisinin bildiği gizli taban.',
            note: 'Teklifler bu tutara ulaştığı anda araç otomatik olarak kesin satışa geçer ve en yüksek teklifi verene satılır. Ulaşmazsa araç “Satılmadı” kapanır.',
          },
          {
            do: 'Artış adımı — her teklifin en az ne kadar artıracağı.',
            note: 'Gönderdiğiniz adım bir **yedektir**, dayatma değil: ihalenin barem tablosu varsa fiyata göre değişen barem adımı işler ve gönderdiğiniz değer hiç kullanılmaz. Yalnızca baremsiz ihalelerde geçerlidir.',
          },
        ],
        warnings: [
          'Kesin satış seçmediyseniz **rezerv vermelisiniz**. Rezervsiz normal bir araç onaylanmaya çalışıldığında işlem hata verir ve araç ihaleye giremez — çünkü geçilemeyecek bir tabanı olan araç hiçbir zaman satılamaz.',
          'Rezervi biz belirlemeyiz. Vermezseniz ihalenin varsayılan rezervi varsa o kullanılır; o da yoksa araç onaylanamaz.',
          'Bir araca özel, baremi geçersiz kılan sabit adım gönderim üzerinden ayarlanamaz — bunu yalnızca Bia yetkilisi tanımlayabilir. İhtiyacınız varsa gönderim öncesinde bizimle konuşun.',
        ],
        see: ['p-fields', 'g-starting-price', 'g-reserve', 'g-guaranteed', 'g-barem'],
      },
      {
        id: 'p-media',
        title: 'Fotoğraf ve video',
        intro: 'İki yol var: adresleri verirsiniz biz indiririz, ya da dosyayı doğrudan siz yüklersiniz.',
        steps: [
          {
            do: 'Adres vererek: gönderimde en fazla 24 fotoğraf adresi iletin.',
            note: 'Dosyaları kendi depomuza kopyalarız. Kendi sunucunuzdaki görseli sonradan silseniz ya da adresi değiştirseniz bile ihaledeki araç görselsiz kalmaz.',
          },
          {
            do: 'Dosya yükleyerek: her bir görseli ayrı ayrı bize gönderin.',
            note: 'Barındırmak istemediğiniz durumda tercih edilir.',
          },
        ],
        fields: [
          { name: 'image/jpeg · image/png · image/webp · image/avif', label: 'Kabul edilen fotoğraf biçimleri', requirement: 'required' },
          { name: 'video/mp4 · video/webm', label: 'Kabul edilen video biçimleri', requirement: 'optional' },
          { name: '10 MB', label: 'Fotoğraf başına üst sınır', requirement: 'required' },
          { name: '100 MB', label: 'Video başına üst sınır', requirement: 'optional' },
          { name: '24', label: 'Tek gönderimde indirilecek en fazla adres', requirement: 'required' },
        ],
        warnings: [
          'Adreslerden indirme işlemi gönderim kaydedildikten **sonra** yapılır. Bu yüzden ulaşılamayan bir adres gönderiminizi geçersiz kılmaz — ama o fotoğraf da gelmez. Hangi adreslerin indirilebildiğini durum sorgusundan görebilirsiniz; kontrol etmezseniz aracınız beş yerine iki fotoğrafla değerlendirilir.',
        ],
        see: ['p-status', 'g-cover', 'g-public-media'],
      },
    ],
  },

  {
    id: 'p-admin-side',
    title: 'Bizde ne oluyor, siz ne görüyorsunuz?',
    intro: 'Gönderdikten sonraki tarafın şeffaf anlatımı.',
    layer: 'business',
    topics: [
      {
        id: 'p-moderation',
        title: 'Bia yetkilisi neyi değerlendiriyor?',
        intro:
          'Başvurunuz yönetim panelindeki “Başvurular” kuyruğuna düşer. Yetkili, gönderdiğiniz aracın tamamını tek ekranda görür.',
        steps: [
          { do: 'Teknik bilgiler, açıklama, donanım ve ekspertiz raporu.' },
          { do: 'Fotoğraflar — ve indirilemeyen adresler varsa onların uyarısı.' },
          {
            do: 'Gizli rezerv dahil fiyat alanları.',
            note: 'Onaylayan kişi bir satış yöneticisidir; neyi onayladığını görmesi gerekir. Rezerv yalnızca katılımcılardan gizlidir.',
          },
          { do: 'Karar: “Onayla” ya da gerekçesiyle birlikte “Reddet”.' },
        ],
        warnings: [
          'Reddedilen başvurunun gerekçesi size durum sorgusunda döner. Gerekçeyi okumadan aynı aracı tekrar göndermeyin — aynı sebeple yine reddedilir.',
        ],
        see: ['p-status', 'h-submissions'],
      },
      {
        id: 'p-status',
        title: 'Durumlar ve ne anlama geldikleri',
        intro: 'Her aracın durumunu istediğiniz an sorgulayabilirsiniz.',
        fields: [
          { name: 'queued', label: 'Bekliyor', requirement: 'optional', note: 'Gönderildi, henüz incelenmedi. Bu aşamada düzeltebilir ya da geri çekebilirsiniz.' },
          { name: 'scheduled', label: 'Sırada', requirement: 'optional', note: 'Onaylandı ve ihaleye eklendi; günü bekliyor.' },
          { name: 'live', label: 'Açık', requirement: 'optional', note: 'Şu anda teklif alıyor.' },
          { name: 'sold', label: 'Satıldı', requirement: 'optional', note: 'Rezerv geçildi ya da kesin satış olarak teklif aldı; kazanan belli.' },
          { name: 'unsold', label: 'Satılmadı', requirement: 'optional', note: 'Rezerv geçilemedi. Araç yeniden gönderilebilir.' },
          { name: 'rejected', label: 'Reddedildi', requirement: 'optional', note: 'Gerekçe durum yanıtında yer alır.' },
        ],
        warnings: [
          'Yalnızca “Bekliyor” durumundaki bir araç düzeltilebilir, geri çekilebilir ya da yeni fotoğraf eklenebilir. Yetkilimiz karar verdikten sonra değişiklik isteğiniz reddedilir — bu noktadan sonra bizimle iletişime geçin.',
          'Bu sınır fotoğrafları da kapsar: onaydan sonra yüklenen bir görsel doğrudan katılımcıya görünürdü, oysa ihaleye giren her şeyin yetkilimizin gördüğü hâl olması gerekir.',
        ],
        see: ['p-moderation', 'p-errors'],
      },
      {
        id: 'p-admin-view',
        title: 'Yönetim panelinde neler görebilirsiniz?',
        intro:
          'Firma kullanıcısı olarak panele girdiğinizde kendi firmanıza açık ihaleleri izleyebilirsiniz. Panel sizin için bir izleme ekranıdır; veri girişi buradan yapılmaz.',
        steps: [
          { do: 'Genel Bakış — kaç ihaleniz aktif, kaç araç satıldı ve toplam satış tutarı.' },
          { do: 'İhaleler — firmanıza açık ihalelerin listesi ve her birinin durumu.' },
          { do: 'Araçlar — havuzdaki araç kayıtları ve teknik bilgileri.' },
          { do: 'İhale sayfası — araçların güncel teklifleri ve sonuçları.' },
        ],
        warnings: [
          'Bütün sayılar ve listeler firmanıza göre süzülür: başka firmalara özel ihaleleri hiçbir ekranda, hiçbir sayıda göremezsiniz.',
          'Gizli rezerv ve teminat bilgileri firma kullanıcısına gösterilmez. Buna karşılık ihale sayfasını açabilen firma rolleri **teklif geçmişini katılımcı adlarıyla birlikte görür** — bu bilgiyi görmemesi gereken personelinize ihale sayfasını açan bir rol vermeyin.',
        ],
        see: ['h-overview', 'h-live-console', 'g-company-gated'],
      },
    ],
  },

  {
    id: 'p-integration',
    title: 'Entegrasyon (teknik)',
    intro:
      'Bu bölüm bağlantıyı kuracak yazılımcı içindir. Ekranlardan yapılacak bir işlem yoktur; tamamı sizin sisteminizden çağrılır. Başlamak için gereken tek şey, Bia yetkilisinin firmanıza verdiği API anahtarıdır.',
    layer: 'technical',
    topics: [
      {
        id: 'p-auth',
        title: 'Kimlik doğrulama ve anahtar',
        intro:
          'Gönderim çağrıları `X-Api-Key` başlığıyla yetkilendirilir — kullanıcı oturumuyla değil. Anahtar **firmanıza** aittir ve size Bia yetkilisi tarafından verilir; ekranlardan ya da bir API çağrısıyla kendiniz üretemezsiniz.',
        code: {
          caption: 'Anahtarın kullanımı',
          body: [
            '# Bütün araç çağrılarında tek başlık:',
            'X-Api-Key: <firmanızın anahtarı>',
            '',
            '# Örnek',
            'GET /api/v2/client/auctions/planned',
            'X-Api-Key: bak_…',
          ].join('\n'),
        },
        warnings: [
          'Anahtarın açık hâli size **yalnızca bir kez** iletilir; bizde yalnızca özeti saklanır. Kaybederseniz kurtarılamaz — yetkilinizden yenisini isteyin.',
          'Anahtar kişiye değil firmaya bağlıdır: personeliniz değişse de entegrasyonunuz çalışmaya devam eder. Aynı firmaya farklı sistemleriniz için birden çok anahtar verilebilir.',
          'Sızma şüphesinde derhal bizi arayın: önce size yeni bir anahtar veririz, sisteminizi ona geçirdikten sonra eskisini iptal ederiz. İptal anında geçerlidir.',
        ],
        see: ['p-endpoints'],
      },
      {
        id: 'p-endpoints',
        title: 'Uç noktalar',
        intro: 'Tamamı `X-Api-Key` ile çağrılır ve yalnızca kendi kayıtlarınıza erişir.',
        fields: [
          { name: 'GET /api/v2/client/auctions/planned', label: 'Gönderim yapılabilecek ihaleler', requirement: 'required', note: 'Yayınlanmış ve listeleme saati henüz gelmemiş olanlar. `auctionId` buradan alınır — listeyi önbelleğe almayın, pencere listeleme saatinde kapanır.' },
          { name: 'POST /api/v2/client/vehicles', label: 'Araç gönderme', requirement: 'required' },
          { name: 'POST /api/v2/client/vehicles/:id/media?kind=…', label: 'Dosya yükleme', requirement: 'optional', note: '`kind` = photo · video · damage_photo · damage_video. Belirtmezseniz `photo` varsayılır — bir videoyu `kind` vermeden gönderirseniz biçim reddedilir (415). Gövde ham ikili veridir; **videolarda `Content-Length` başlığı zorunludur** (yoksa 411). Yalnızca araç “Bekliyor” durumundayken çalışır: karar verildikten sonra 409 döner.' },
          { name: 'PATCH /api/v2/client/vehicles/:id', label: 'Bekleyen aracı düzeltme', requirement: 'optional', note: 'Araç bilgilerini ve fiyatları düzeltir. **Hedef ihale ve satış tipi değiştirilemez** — bunlar gövdede gönderilse bile yok sayılır. Karar verildikten sonra 409 döner.' },
          { name: 'DELETE /api/v2/client/vehicles/:id', label: 'Bekleyen aracı geri çekme', requirement: 'optional' },
          { name: 'GET /api/v2/client/vehicles/:id/status', label: 'Durum sorgulama', requirement: 'required', note: 'Kalıcı araç kimliğiyle çalışır; satılmayıp tekrar eklenen araçta da aynı kimlik geçerlidir.' },
        ],
        code: {
          caption: 'Asgari gönderim',
          body: [
            'POST /api/v2/client/vehicles',
            'X-Api-Key: <key>',
            '{',
            '  "auctionId": "…",',
            '  "plate": "34ABC001",',
            '  "startingPrice": 15000000,   // 150.000,00 ₺',
            '  "reserve": 18000000,         // gizli taban',
            '  "brand": "Volkswagen",',
            '  "series": "Passat",',
            '  "year": 2021,',
            '  "km": 68000,',
            '  "mediaUrls": ["https://…/1.jpg", "https://…/2.jpg"]',
            '}',
            '',
            '→ { "id": "…", "vehicleId": "…", "status": "queued", … }',
          ].join('\n'),
        },
        warnings: [
          'Durum sorgusunu `vehicleId` ile yapın, gönderim kimliğiyle değil: araç satılmayıp tekrar ihaleye alındığında gönderim kaydı değişir, araç kimliği sabit kalır.',
          'Yanlış ihaleye ya da yanlış satış tipiyle gönderdiyseniz düzeltme çağrısı işe yaramaz; bu iki alan sessizce yok sayılır ve çağrı yine de başarılı döner. Araç hâlâ “Bekliyor” durumundayken geri çekip doğru değerlerle yeniden gönderin.',
        ],
        see: ['p-fields', 'p-errors'],
      },
      {
        id: 'p-errors',
        title: 'Hata yanıtları ve anlamları',
        intro: 'Gördüğünüz kodun karşılığı ve yapılacak iş.',
        fields: [
          { name: '409 · Auction is not open for submissions', label: 'İhale gönderime kapalı', requirement: 'required', note: 'İhale yayında değil ya da listeleme saati geçmiş. En sık sebep, uygun ihaleler listesinin eski olmasıdır — listeyi yeniden alın.' },
          { name: '409 · Plate already registered', label: 'Plaka başkasına kayıtlı', requirement: 'required', note: 'Aynı plaka başka bir iş ortağının kaydında. Bizimle iletişime geçin.' },
          { name: '409 · Vehicle already has an active submission', label: 'Araç zaten sırada', requirement: 'required', note: 'Aynı araç için ikinci gönderim yapmayın; önce mevcut başvurunun sonucunu bekleyin.' },
          { name: '409 · already been sold', label: 'Araç satılmış', requirement: 'required', note: 'Satılan araç yeniden gönderilemez.' },
          { name: '409 · can no longer be edited / removed', label: 'Karar verilmiş', requirement: 'required', note: 'Düzeltme ve geri çekme yalnızca “Bekliyor” durumunda çalışır.' },
          { name: '400', label: 'Geçersiz gövde', requirement: 'required', note: 'Genellikle para alanının kuruş tam sayısı olmaması ya da zorunlu alanın eksikliği.' },
          {
            name: '401 · Invalid or revoked API key',
            label: 'Anahtar geçersiz',
            requirement: 'required',
            note: 'Anahtar yanlış, iptal edilmiş ya da firmanız arşivlenmiş olabilir — üçü de aynı yanıtı döner. Anahtarı yeniden yazmak yerine bizimle iletişime geçin; gerekiyorsa yeni anahtar veririz.',
          },
        ],
        warnings: [
          'Gönderim başarılı dönse bile işiniz bitmedi: fotoğraf indirme sonuçları ve onay/ret kararı sonradan oluşur. Durum sorgusunu düzenli çalıştırmayan entegrasyonlar reddedilen araçları fark etmez.',
        ],
        see: ['p-status', 'p-media'],
      },
    ],
  },
];
